// ==============================
// File: src/components/AI/MudoAssistant.jsx
// ==============================
import React, { useEffect, useRef, useState } from "react";
import { apiFetch } from "../../lib/apiFetch.js";
import { useAccess } from "../../contexts/AccessContext";

export default function MudoAssistant({
  endpoint = "/api/ceramics-chat",
  context,
}) {
  const { isUnlocked } = useAccess() || {};
  
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! Ask me anything about clay, glazes, firing, or Mudo.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll chat to bottom on new messages
  useEffect(() => {
    scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
  }, [messages]);
  
  // Don't render AI assistant if user is not unlocked (no active subscription/trial)
  if (!isUnlocked) return null;

  async function sendMessage() {
    if (!input.trim() || loading) return;

    const userMessage = { role: "user", content: input.trim() };
    setMessages((m) => [...m, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const payloadMessages = [
        ...(context ? [{ role: "user", content: `Context: ${context}` }] : []),
        ...messages,
        userMessage,
      ];

      const res = await apiFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({ messages: payloadMessages }),
      });

      const text = await res.text();

      if (!res.ok) {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content:
              text ||
              `Server error (${res.status}). The assistant could not respond.`,
          },
        ]);
        return;
      }

      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: text || "(no reply)",
        },
      ]);
    } catch (err) {
      console.error("[MudoAssistant] fetch error:", err);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry, I couldn’t reach the server. Please check your connection or API URL.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        zIndex: 1000,
        fontFamily: "Inter, system-ui, Avenir, Helvetica, Arial, sans-serif",
      }}
    >
      {!open && (
        <button
            onClick={() => setOpen(true)}
            aria-label="Mudo AI"
            style={{
              position: "fixed",
              right: 16,
            bottom: 16,
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "var(--accent, #3A9C7A)",
            color: "#000",
            border: "none",
            boxShadow: "0 10px 25px rgba(0,0,0,.35)",
            cursor: "pointer",
            zIndex: 60,
            fontSize: "11px",
            fontWeight: 600,
          }}
          >
            Mudo AI
          </button>
        )}

      {open && (
        <div
          style={{
            width: 340,
            height: 460,
            background: "#1c1c1c",
            color: "#fff",
            borderRadius: 16,
            boxShadow: "0 6px 16px rgba(0,0,0,0.3)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "var(--accent, #3A9C7A)",
              padding: "10px",
              fontWeight: 600,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Mudo Assistant</span>
            <button
              onClick={() => setOpen(false)}
            style={{
              background: "transparent",
              border: "none",
              color: "#000",
              fontSize: 16,
              cursor: "pointer",
            }}
            aria-label="Close"
          >
              X
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: 10,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {messages.map((m, i) => {
              const isAssistant = m.role === "assistant";
              return (
                <div
                  key={i}
                  style={{
                    alignSelf: isAssistant ? "flex-start" : "flex-end",
                    background: isAssistant
                      ? "rgba(255,255,255,0.1)"
                      : "#b05640",
                    padding: "8px 12px",
                    borderRadius: 12,
                    maxWidth: "80%",
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.35,
                    fontSize: 14,
                  }}
                >
                  {m.content}
                </div>
              );
            })}
            {loading && <div style={{ opacity: 0.7 }}>…thinking…</div>}
          </div>

          {/* Input */}
          <div style={{ padding: 10, display: "flex", gap: 8 }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Ask about glazes, firing, costs..."
              style={{
                flex: 1,
                borderRadius: 8,
                padding: "8px 10px",
                border: "1px solid #444",
                background: "#2a2a2a",
                color: "white",
                outline: "none",
              }}
            />
            <button
              onClick={sendMessage}
              disabled={loading}
              style={{
                background: "var(--accent, #3A9C7A)",
                color: "#000",
                border: "none",
                borderRadius: 8,
                padding: "8px 12px",
                cursor: "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
